"use client";

import { useEffect, useState } from "react";
import styles from "./page.module.css";
import PlaylistSlider from "@/components/PlaylistSlider";
import SectionWithPlayAll from "@/components/SectionWithPlayAll";
import Head from "next/head";

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

export default function HomePageClient({
  userId,
  latestPlaylists,
  latestSongs,
  topPlaylists,
  musicItems,
  randomPlaylists,
  recommendations,
  recentlyPlayed,
  globalRecent,
  youtubePlaylists,
}) {
  const [shuffledRandom, setShuffledRandom] = useState(randomPlaylists);

  useEffect(() => {
    setShuffledRandom(shuffle(randomPlaylists));
  }, [randomPlaylists]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Mira Classic",
    url: "https://miraclassic.com",
    description:
      "A calm space for classical music — high-quality audio, curated works, playlists, and personalized recommendations.",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://miraclassic.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
    publisher: {
      "@type": "Organization",
      name: "Mira Classic",
      url: "https://miraclassic.com",
      logo: {
        "@type": "ImageObject",
        url: "https://miraclassic.com/images/mc.png",
      },
    },
    about: {
      "@type": "MusicPlaylist",
      name: "Classical music playlists and works",
      url: "https://miraclassic.com",
    },
  };
  const sectionOrder = userId
    ? [
        "recommendations",
        "recentlyPlayed",
        "newReleases",
        "youtube",
        "topPlaylists",
        "songs",
        "playlists",
      ]
    : [
        "youtube",
        "globalRecent",
        "newReleases",
        "topPlaylists",
        "songs",
        "playlists",
      ];
  const renderSection = (section: string) => {
    switch (section) {
      case "recommendations":
        return (
          <SectionWithPlayAll
            key={section}
            title="Recommended for You"
            playlists={(recommendations.length > 0
              ? recommendations
              : globalRecent
            ).filter((item) => item._type === "playlist")}
            songs={(recommendations.length > 0
              ? recommendations
              : globalRecent
            ).filter((item) => item._type === "song")}
          >
            <PlaylistSlider
              playlists={(recommendations.length > 0
                ? recommendations
                : globalRecent
              ).filter((item) => item._type === "playlist")}
              songs={(recommendations.length > 0
                ? recommendations
                : globalRecent
              ).filter((item) => item._type === "song")}
            />
          </SectionWithPlayAll>
        );

      case "recentlyPlayed":
        return recentlyPlayed.length > 0 ? (
          <SectionWithPlayAll
            key={section}
            title="Recently Played"
            songs={recentlyPlayed}
          >
            <PlaylistSlider items={recentlyPlayed} />
          </SectionWithPlayAll>
        ) : null;

      case "globalRecent":
        return (
          <SectionWithPlayAll
            key={section}
            title="Latest Played Globally"
            songs={globalRecent}
          >
            <PlaylistSlider items={globalRecent} />
          </SectionWithPlayAll>
        );

      case "newReleases":
        return (
          <SectionWithPlayAll
            key={section}
            title="New Releases"
            songs={latestSongs}
            playlists={latestPlaylists}
          >
            <PlaylistSlider
              title="New Releases"
              playlists={latestPlaylists}
              songs={latestSongs}
            />
          </SectionWithPlayAll>
        );

      case "youtube":
        return (
          <SectionWithPlayAll
            key={section}
            title="Popular on YouTube"
            playlists={youtubePlaylists}
          >
            <PlaylistSlider playlists={youtubePlaylists} type="playlist" />
          </SectionWithPlayAll>
        );

      case "topPlaylists":
        return (
          <SectionWithPlayAll
            key={section}
            title="Top Playlists"
            playlists={topPlaylists}
            songs={topPlaylists}
          >
            <PlaylistSlider playlists={topPlaylists} type="playlist" />
          </SectionWithPlayAll>
        );

      case "songs":
        return (
          <SectionWithPlayAll key={section} title="Songs" songs={musicItems}>
            <PlaylistSlider items={musicItems} />
          </SectionWithPlayAll>
        );

      case "playlists":
        return (
          <SectionWithPlayAll
            key={section}
            title="Playlists"
            playlists={shuffledRandom}
            songs={shuffledRandom}
          >
            <PlaylistSlider playlists={shuffledRandom} type="playlist" />
          </SectionWithPlayAll>
        );

      default:
        return null;
    }
  };
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData),
          }}
        />
      </Head>

      <div className={styles.main}>
        <h1 className={styles.pageTitle}>Home</h1>

        {sectionOrder.map(renderSection)}
      </div>
    </>
  );
}
